import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Modal, Form, ListGroup } from "react-bootstrap";
import { listOrganizations, searchZohoAccounts, createOrganization, listAccessRequests, updateAccessRequest } from "../../services/orgService";
import { errorToast, successToast } from "../Toasts/Toasts";

// CMS: HortiHub organizations. Created from a Zoho CRM Account - search the
// CRM, pick the Account, done - so the org and the CRM never disagree.

const TYPES = ["society", "school", "college", "mall", "corporate", "hospital", "hotel", "institution", "commercial", "fm_company"];

const CreateFromZohoModal = ({ show, onClose, onCreated, initialQuery = "" }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [type, setType] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (show) { setQ(initialQuery); setResults([]); setPicked(null); } }, [show, initialQuery]);

  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try { setResults(await searchZohoAccounts(q.trim())); } catch (err) { errorToast(err?.message || "Search failed"); } finally { setBusy(false); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const r = await createOrganization({ zohoAccountId: picked.id, type: type || undefined });
      if (r?.success) { successToast(`Created ${r.data.name}`); onCreated(r.data); onClose(); }
      else errorToast(r?.message);
    } catch (err) { errorToast(err?.message || "Could not create"); } finally { setBusy(false); }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton><Modal.Title>New organization from Zoho CRM</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form onSubmit={search} className="d-flex gap-2 mb-3">
          <Form.Control placeholder="Account name, e.g. Gaur City" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          <Button type="submit" variant="outline-success" disabled={busy}>Search</Button>
        </Form>
        <ListGroup className="mb-3">
          {results.map((a) => (
            <ListGroup.Item key={a.id} action active={picked?.id === a.id} onClick={() => setPicked(a)}>
              <div className="fw-semibold">{a.name}</div>
              <div className="small text-muted">{[a.category, a.city].filter(Boolean).join(" · ")}</div>
            </ListGroup.Item>
          ))}
          {!results.length && q && !busy && <ListGroup.Item className="text-muted">No matches yet — search, then pick one.</ListGroup.Item>}
        </ListGroup>
        {picked && (
          <Form.Group>
            <Form.Label>Type (defaults from the Account category)</Form.Label>
            <Form.Select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">— from CRM —</option>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Form.Select>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" disabled={!picked || busy} onClick={create}>Create {picked ? picked.name : ""}</Button>
      </Modal.Footer>
    </Modal>
  );
};

// Requests from the hortihub.in landing page. MG calls, creates the org from
// its Zoho Account, invites the number - then marks the request converted.
const AccessRequests = ({ onCreateFor }) => {
  const [rows, setRows] = useState([]);
  const [all, setAll] = useState(false);
  const load = useCallback(async () => {
    try { setRows(await listAccessRequests(all ? {} : { status: "new" })); } catch (e) { /* section is optional */ }
  }, [all]);
  useEffect(() => { load(); }, [load]);
  const mark = async (r, status) => {
    try { const x = await updateAccessRequest(r.id, { status }); if (x?.success) load(); else errorToast(x?.message); } catch (e) { errorToast(e?.message); }
  };
  if (!rows.length && !all) return null;
  return (
    <div className="col-12">
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-bold mb-0">Access requests {rows.length ? <span className="badge bg-warning text-dark">{rows.length}</span> : null}</h6>
            <Form.Check type="switch" id="allReq" label="Show all" checked={all} onChange={(e) => setAll(e.target.checked)} className="small" />
          </div>
          <table className="table table-sm mb-0">
            <thead><tr><th>Organization</th><th>Contact</th><th>City</th><th>Message</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.orgName}<div className="small text-muted">{r.orgType}</div></td>
                  <td>{r.contactName}<div className="small text-muted">+{r.phone}{r.email ? ` · ${r.email}` : ""}</div></td>
                  <td>{r.city || "—"}</td>
                  <td className="small">{r.message || "—"}</td>
                  <td><span className="badge bg-light text-dark">{r.status}</span></td>
                  <td className="text-nowrap">
                    {r.status === "new" && <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => mark(r, "contacted")}>Contacted</Button>}
                    {r.status !== "converted" && r.status !== "declined" && <Button size="sm" variant="outline-success" className="me-1" onClick={() => onCreateFor(r)}>Create org</Button>}
                    {r.status !== "converted" && r.status !== "declined" && <Button size="sm" variant="outline-danger" onClick={() => mark(r, "declined")}>Decline</Button>}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="text-muted small">None.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const OrganizationList = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [fromRequest, setFromRequest] = useState(null);

  const load = useCallback(async () => {
    try { setRows(await listOrganizations(q ? { q } : {})); } catch (e) { errorToast("Could not load organizations"); }
  }, [q]);
  useEffect(() => { load(); }, [load]);
  const onCreated = async (org) => {
    if (fromRequest) { try { await updateAccessRequest(fromRequest.id, { status: "converted", organizationId: org.id }); } catch (e) { /* non-fatal */ } setFromRequest(null); }
    load();
  };

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row g-3">
          <div className="col-12">
            <div className="row-title px-2 d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mb-0">HortiHub Organizations</h6>
              <div className="d-flex gap-2">
                <Form.Control size="sm" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 220 }} />
                <Button variant="outline-success" size="sm" onClick={() => setShow(true)}>New from Zoho</Button>
              </div>
            </div>
          </div>
          <AccessRequests onCreateFor={(r) => { setFromRequest(r); setShow(true); }} />
          <div className="col-12">
            <div className="card">
              <div className="card-body p-0">
                <table className="table table-hover mb-0">
                  <thead><tr><th>Name</th><th>Type</th><th>Sites</th><th>Zoho</th><th>Status</th></tr></thead>
                  <tbody>
                    {rows.map((o) => (
                      <tr key={o.id}>
                        <td><Link to={`/organizations/${o.id}`}>{o.name}</Link></td>
                        <td>{o.type}</td>
                        <td>{o.sites?.length || 0}</td>
                        <td>{o.zohoAccountId ? "linked" : <span className="text-warning">not linked</span>}</td>
                        <td>{o.status}</td>
                      </tr>
                    ))}
                    {!rows.length && <tr><td colSpan={5} className="text-muted text-center py-4">No organizations yet. Create the first one from a Zoho Account.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CreateFromZohoModal show={show} onClose={() => { setShow(false); setFromRequest(null); }} onCreated={onCreated} initialQuery={fromRequest ? fromRequest.orgName : ""} />
    </div>
  );
};

export default OrganizationList;
